import { Link } from "react-router-dom";
import { Building2, MapPin, Eye, Phone, MessageSquare, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PropertyListing } from "@/data/listings";
import { PROPERTY_CATEGORY_LABELS } from "@/lib/listing-quality";

interface PropertyCardProps {
  property: PropertyListing;
}

export function PropertyCard({ property }: PropertyCardProps) {
  const primaryImage = property.images && property.images.length > 0
    ? property.images[0]
    : "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=800&q=80";

  return (
    <Card className="overflow-hidden group hover:shadow-xl transition-all duration-300 border border-border/80 bg-card flex flex-col justify-between rounded-2xl">
      <div className="relative aspect-[16/10] overflow-hidden bg-muted">
        <img
          src={primaryImage}
          alt={property.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3 flex gap-2">
          <span className={`px-3 py-1 text-xs font-semibold rounded-full uppercase tracking-wider text-white shadow-md ${
            property.type === 'satilik' ? 'bg-primary' : 'bg-secondary text-secondary-foreground'
          }`}>
            {property.type === 'satilik' ? 'Satılık' : 'Kiralık'}
          </span>
          <span className="px-3 py-1 text-xs font-medium rounded-full bg-background/90 text-foreground shadow-sm backdrop-blur-sm">
            {(PROPERTY_CATEGORY_LABELS[property.category] || 'Daire').toLocaleUpperCase('tr-TR')}
          </span>
        </div>
        {property.featured && (
          <div className="absolute top-3 right-3">
            <span className="px-2.5 py-1 text-xs font-bold rounded-md bg-amber-500 text-white shadow-sm">
              Fırsat Portföy
            </span>
          </div>
        )}
        <div className="absolute bottom-3 left-3 right-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2 rounded-b-md">
          <p className="text-xl font-extrabold text-white">
            {property.price.toLocaleString('tr-TR')} {property.currency}
          </p>
        </div>
      </div>

      <CardContent className="p-5 flex-1 flex flex-col justify-between space-y-4">
        <div className="space-y-2">
          <div className="flex items-center gap-1 text-xs font-bold text-primary uppercase tracking-wider">
            <MapPin className="h-3.5 w-3.5" />
            <span>Adana / {property.ilce} / {property.mahalle}</span>
          </div>

          <h3 className="font-extrabold text-base text-foreground group-hover:text-primary transition-colors line-clamp-2 leading-snug">
            <Link to={`/ilan/${property.slug}`}>
              {property.title}
            </Link>
          </h3>

          <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
            {property.description}
          </p>
        </div>

        <div className="pt-3 border-t border-border/60 space-y-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground font-semibold">
            {property.roomCount && <span>{property.roomCount}</span>}
            <span>{property.netArea} m² Net</span>
            {property.category !== 'arsa' && <span>{property.buildingAge}</span>}
          </div>

          <div className="flex items-center justify-between pt-2">
            <Button asChild size="sm" variant="outline" className="w-full font-bold">
              <Link to={`/ilan/${property.slug}`}>
                İlanı İncele <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Link>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
